/**
 * Monitors (`/monitors`) — scheduled-audit surface.
 *
 * Replacement for the schedule-editing UI that lived in legacy
 * `SiteDetailIsland.tsx`. Per `05-pages.md` §5c and `topology.md`
 * §1, this is now a top-level nav entry: a flat list of everything
 * the workspace has set to auto-run.
 *
 * Data comes from Stage 4's `useMonitors()` (client-side aggregator
 * over `/v1/sites` + per-site `/settings`, Gap M1). The list keeps
 * "paused" monitors (`schedule === null && monitoring_enabled`) so
 * the UI can render a Resume CTA.
 *
 * Actions today: pause/resume (toggles `monitoring_enabled`), edit
 * (→ `/monitors/new?siteUuid=…`), view site (→ `/origins/:uuid`).
 * Real schedule edits live on the stepper so the two flows share
 * code; we flag this as follow-up in the handover.
 */
import { EmptyState } from '@/components/primitives/EmptyState';
import { ErrorState } from '@/components/primitives/ErrorState';
import { FadedCover } from '@/components/primitives/FadedCover';
import { StatusBadge } from '@/components/primitives/StatusBadge';
import { UpsellInline } from '@/components/primitives/UpsellInline';
import { Island } from '@/components/shell/Island';
import { Button } from '@/components/ui/button';
import { Menu, MenuItem, MenuPopup, MenuTrigger } from '@/components/ui/menu';
import { Skeleton } from '@/components/ui/skeleton';
import type { Monitor } from '@/lib/api-types';
import { toastFromError, toastSuccess } from '@/lib/errors';
import { hostOf } from '@/lib/format';
import { useEntitlement, useMonitors, useToggleMonitor, useUsage } from '@/lib/queries';
import { cn } from '@/lib/utils';
import {
  DotsThreeVertical,
  Globe,
  Pause,
  PencilSimple,
  Play,
  Plus,
  Pulse,
} from '@phosphor-icons/react';
import { Link, createFileRoute } from '@tanstack/react-router';
import type { ReactElement } from 'react';

export const Route = createFileRoute('/_app/monitors/')({
  component: MonitorsPage,
});

function MonitorsPage(): ReactElement {
  const monitors = useMonitors();
  const usage = useUsage();
  const entitlement = useEntitlement('create_monitor');
  const isUnpaid = usage.data?.plan === null;

  const items = monitors.data ?? [];

  // Decide which "New monitor" CTA to render. Authenticated users
  // on a paid plan get the normal link; unpaid users get an
  // upsell button that routes to billing rather than a stepper
  // whose final Save would 402.
  const newMonitorCta = entitlement.isLoading ? null : entitlement.allowed ? (
    <Button render={<Link to="/monitors/new" />}>
      <Plus aria-hidden="true" />
      New monitor
    </Button>
  ) : (
    <UpsellInline variant="button" minPlan={entitlement.minPlan} />
  );

  return (
    <Island breadcrumb={[{ label: 'Monitors' }]} actions={newMonitorCta}>
      <div className="space-y-4">
        <header className="space-y-1">
          <p className="text-sm text-[var(--color-mute)]">
            Schedule recurring Core Web Vitals checks against any URL. Paused monitors stay in the
            list so you can resume them later.
          </p>
        </header>

        {monitors.isLoading && (
          <div className="space-y-2">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        )}

        {monitors.isError && !monitors.isLoading && (
          <ErrorState
            title="We couldn't load monitors"
            error={monitors.error}
            retry={() => {
              void monitors.refetch();
            }}
          />
        )}

        {!monitors.isLoading && !monitors.isError && items.length === 0 && (
          <EmptyState
            icon={Pulse}
            title="No monitors yet"
            description="Monitor any URL or page to track Core Web Vitals over time."
            action={
              entitlement.isLoading ? undefined : entitlement.allowed ? (
                <Button render={<Link to="/monitors/new" />}>
                  <Plus aria-hidden="true" />
                  New monitor
                </Button>
              ) : (
                <UpsellInline variant="button" minPlan={entitlement.minPlan} />
              )
            }
          />
        )}

        {!monitors.isLoading && !monitors.isError && items.length > 0 && (
          <FadedCover
            faded={isUnpaid && items.length > 1}
            upsellHref="/settings/billing"
            upsellLabel="Upgrade to run multiple monitors →"
          >
            <section className="overflow-hidden rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)]">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-[var(--color-line-soft)] text-left text-xs uppercase tracking-[0.06em] text-[var(--color-mute)]">
                      <th className="px-4 py-2 font-medium">URL</th>
                      <th className="px-4 py-2 font-medium">Schedule</th>
                      <th className="px-4 py-2 font-medium">Regions</th>
                      <th className="px-4 py-2 font-medium">Status</th>
                      <th className="w-8 px-4 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((m) => (
                      <MonitorRow key={m.site_uuid} monitor={m} />
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </FadedCover>
        )}
      </div>
    </Island>
  );
}

function MonitorRow({ monitor }: { monitor: Monitor }): ReactElement {
  const toggle = useToggleMonitor();
  const isActive = monitor.monitoring_enabled && monitor.schedule !== null;
  const isPaused = !monitor.monitoring_enabled || monitor.schedule === null;

  async function onToggle() {
    try {
      await toggle.mutateAsync({
        siteUuid: monitor.site_uuid,
        monitoring_enabled: !monitor.monitoring_enabled,
      });
      toastSuccess(
        monitor.monitoring_enabled ? 'Monitor paused' : 'Monitor resumed',
        monitor.site_name,
      );
    } catch (err) {
      toastFromError(err);
    }
  }

  return (
    <tr className="border-b border-[var(--color-line-soft)]/70 transition-colors hover:bg-[var(--color-paper-alt)]/60">
      <td className="px-4 py-3">
        <Link
          to="/origins/$siteUuid"
          params={{ siteUuid: monitor.site_uuid }}
          className="block min-w-0"
        >
          <div className="truncate text-sm font-medium text-[var(--color-ink)] hover:underline">
            {monitor.site_name}
          </div>
          <div className="truncate text-xs text-[var(--color-mute)]">
            {hostOf(monitor.site_url)}
          </div>
        </Link>
      </td>
      <td className="px-4 py-3">
        {monitor.schedule ? (
          <span className="font-mono text-xs text-[var(--color-ink-soft)]">{monitor.schedule}</span>
        ) : (
          <span className="text-xs text-[var(--color-mute)]">—</span>
        )}
      </td>
      <td className="px-4 py-3 text-xs text-[var(--color-mute)]">
        {
          // `region_uuids` is occasionally absent from the current
          // API payload while the monitors slice is mid-rewrite
          // (plans/monitors/04-dashboard.md, owned by phase 4).
          // Treat missing as zero so the page renders instead of
          // throwing `Cannot read properties of undefined (reading
          // 'length')` on every mount. Remove the `?? []` once the
          // wholesale rewrite lands and `region_uuids: string[]` is
          // guaranteed by the response contract.
        }
        {(() => {
          const count = (monitor.region_uuids ?? []).length;
          if (count === 0) return '—';
          return `${count} region${count === 1 ? '' : 's'}`;
        })()}
      </td>
      <td className="px-4 py-3">
        {isActive ? (
          <StatusBadge status="success" />
        ) : (
          <span
            className={cn(
              'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
              'bg-[var(--color-paper-alt)] text-[var(--color-ink-soft)]',
            )}
          >
            paused
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-right">
        <Menu>
          <MenuTrigger
            render={<Button size="icon-sm" variant="ghost" aria-label="Monitor actions" />}
          >
            <DotsThreeVertical aria-hidden="true" />
          </MenuTrigger>
          <MenuPopup>
            <MenuItem
              onClick={() => {
                void onToggle();
              }}
            >
              {isPaused ? (
                <>
                  <Play aria-hidden="true" />
                  Resume
                </>
              ) : (
                <>
                  <Pause aria-hidden="true" />
                  Pause
                </>
              )}
            </MenuItem>
            <MenuItem render={<Link to="/monitors/new" search={{ siteUuid: monitor.site_uuid }} />}>
              <PencilSimple aria-hidden="true" />
              Edit schedule
            </MenuItem>
            <MenuItem
              render={<Link to="/origins/$siteUuid" params={{ siteUuid: monitor.site_uuid }} />}
            >
              <Globe aria-hidden="true" />
              View site
            </MenuItem>
          </MenuPopup>
        </Menu>
      </td>
    </tr>
  );
}
